import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { ExternalLink, Lightbulb, Loader2, TriangleAlert } from 'lucide-react';
import { toast } from 'sonner';
import {
  ApiError,
  cn,
  createAccountGuard,
  createFeedbackQueue,
  describeDevice,
  isConnectionFailure,
  MY_REPORTS_URL,
  type BuildInfo,
  type FeedbackKind,
  type FeedbackReport,
  type FeedbackScreenshot,
} from '@higgins/utils';
import { captureDiagnostics } from '../lib/feedback-diagnostics';
import { OutboundLink } from './outbound-link';
import { ScreenshotPicker } from './screenshot-picker';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';

export interface FeedbackFormProps {
  /**
   * Sends one report to the app's own API (`POST /api/feedback`), resolving to
   * its reference. The app key, the user and the app's status are added on the
   * server, never sent from here. Pass a stable function (a module-level one):
   * a new function makes a new queue.
   */
  send: (report: FeedbackReport) => Promise<{ reference: string }>;
  /**
   * The signed-in account's Entra object id (MSAL `account.localAccountId`). Reports saved
   * offline are kept per account and only ever sent while that account is signed in: on a
   * shared iPad, one person's saved report never goes under the next person's name. Until it
   * is known nothing is sent or queued.
   */
  accountId: string | undefined;
  /**
   * The app's catalogue key ('measure-to-quote'). Saved reports are kept per app as well as per
   * account, so a report saved in one app is never sent through another. (The server still
   * records the app from its own setting.)
   */
  appKey: string;
  /** The job the user is in, when the app has one (Measure2Quote's job number). */
  jobRef?: string;
  /** The app's build label; defaults to the deploy's `VITE_APP_VERSION`. */
  appBuild?: string;
  /** A plain name for the screen the user is on ("Take off"), recorded with the path. */
  screenName?: string;
  /** Changes still waiting to sync to CRM (Measure2Quote's outbox), read when the user presses Send. */
  changesToSync?: () => Promise<number>;
  /**
   * The app the report is about when it isn't this one (App Feedback's report page for an app
   * with no API): its build and the page the user was on there.
   */
  otherApp?: { build: BuildInfo; path: string };
  /** Where "See my reports" goes after a send; default App Feedback's My reports in a new tab. */
  myReportsLink?: ReactNode;
}

const MAX_WORDS = 10000;
const HTTP_UNAUTHORISED = 401;
const HTTP_TOO_MANY = 429;
const HTTP_SERVER_ERROR = 500;

/** No signal, signed out for a moment, or the server busy: keep it and try later. Anything else is a refusal. */
function isRetryable(err: unknown): boolean {
  if (isConnectionFailure(err)) return true;
  return err instanceof ApiError && (err.status === HTTP_UNAUTHORISED || err.status === HTTP_TOO_MANY || err.status >= HTTP_SERVER_ERROR);
}

function deviceStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

type FeedbackQueue = ReturnType<typeof createFeedbackQueue>;

/** Send whatever this account saved while it had no signal, and say what happened to it. */
async function sendWaiting(queue: FeedbackQueue): Promise<void> {
  if (queue.waiting() === 0) return;
  const { sent, dropped } = await queue.flush();
  for (const s of sent) toast.success(`Your saved report was sent: ${s.reference}.`);
  for (const d of dropped) toast.error(`A saved report couldn't be sent and was removed: ${d.message}`);
}

type Outcome = { kind: 'sent'; reference: string } | { kind: 'queued' } | { kind: 'refused'; message: string };

/**
 * The report's draft, its offline queue and its send, for the header's button (where it outlives
 * the side panel, so closing the panel keeps the draft) and App Feedback's report page.
 * Everything but the kind, the words and the pictures (page, device, build, job, time) is
 * captured here or on the server. With no signal the report is kept on the device and sent when
 * the signal returns.
 */
export function useFeedbackForm(props: FeedbackFormProps, onAccountChange?: () => void) {
  const { send, accountId, appKey, jobRef, appBuild, screenName, changesToSync, otherApp } = props;
  const [kind, setKind] = useState<FeedbackKind | null>(null);
  const [words, setWords] = useState('');
  const [screenshots, setScreenshots] = useState<FeedbackScreenshot[]>([]);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  // One queue per app, account AND sender, each holding its own sender from the start: a new
  // account (or a new sender) gets a new queue, so a queue only ever sends with its own sender.
  const queue = useMemo<FeedbackQueue | null>(
    () =>
      accountId
        ? createFeedbackQueue({
            appKey,
            accountId,
            send: async (report) => (await send(report)).reference,
            isRetryable,
            isNoSignal: isConnectionFailure,
            storage: deviceStorage(),
          })
        : null,
    [appKey, accountId, send],
  );

  // Flush only once the account is known, and only that account's reports. When the account
  // changes, the old queue is stopped: a flush still waiting on the network sends nothing more.
  useEffect(() => {
    if (!queue) return;
    void sendWaiting(queue);
    const onOnline = () => void sendWaiting(queue);
    window.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener('online', onOnline);
      queue.stop();
    };
  }, [queue]);

  function clear() {
    setKind(null);
    setWords('');
    setScreenshots([]);
    setOutcome(null);
    setBusy(false);
  }

  // A new account (sign-out, or someone else signed in without a reload) clears the draft:
  // one person's words and pictures never show for, or send as, the next.
  const [guard] = useState(() => createAccountGuard(accountId));
  useEffect(() => {
    if (!guard.switchTo(accountId)) return;
    clear();
    onAccountChange?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs on an account change only
  }, [guard, accountId]);

  async function submit() {
    if (!queue || !kind || !words.trim()) return;
    setBusy(true);
    setOutcome(null);
    // Read before the send, so the recent errors are what the user saw, not this report's own.
    const diagnostics = await captureDiagnostics({ screenName, changesToSync, otherApp });
    const report: FeedbackReport = {
      id: crypto.randomUUID(),
      kind,
      description: words.trim(),
      appBuild: appBuild ?? diagnostics.build.version,
      pagePath: otherApp?.path ?? window.location.pathname,
      device: describeDevice(navigator.userAgent, navigator.maxTouchPoints),
      jobRef,
      capturedOn: new Date().toISOString(),
      ...(screenshots.length > 0 ? { screenshots } : {}),
      diagnostics,
    };
    // The account this submission belongs to: if another account is signed in by the time it
    // finishes, its result is ignored and never shown on the new person's panel.
    const owner = accountId;
    let next: Outcome;
    try {
      const result = await queue.submit(report);
      next = result.status === 'sent' ? { kind: 'sent', reference: result.reference } : { kind: 'queued' };
    } catch (err) {
      next = { kind: 'refused', message: err instanceof Error ? err.message : String(err) };
    }
    if (!guard.isCurrent(owner)) return;
    setOutcome(next);
    setBusy(false);
  }

  return {
    kind, setKind, words, setWords, screenshots, setScreenshots, busy, outcome,
    ready: queue !== null,
    /** True once the report has gone or been kept: closing then starts a new one. */
    finished: outcome !== null && outcome.kind !== 'refused',
    submit, clear,
  };
}

export type FeedbackFormState = ReturnType<typeof useFeedbackForm>;

/** The form itself: Problem or Suggestion, one text box, pictures, Send; then the outcome. */
export function FeedbackFormView({ form, onDone, myReportsLink }: { form: FeedbackFormState; onDone: () => void; myReportsLink?: ReactNode }) {
  const { outcome } = form;
  if (outcome && outcome.kind !== 'refused') {
    return (
      <div className="space-y-4">
        {outcome.kind === 'sent' ? (
          <>
            <p className="text-base">
              Thanks, your report is <strong>{outcome.reference}</strong>.
            </p>
            <p className="text-sm">{myReportsLink ?? <MyReportsLink />}</p>
          </>
        ) : (
          <p className="text-base">No signal. Your report is saved on this device and will be sent when you're back online.</p>
        )}
        <Button onClick={onDone} className="w-full">
          Close
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3" role="radiogroup" aria-label="Problem or suggestion">
        <KindButton selected={form.kind === 'problem'} onSelect={() => form.setKind('problem')} icon={<TriangleAlert className="h-6 w-6" />} label="Problem" />
        <KindButton selected={form.kind === 'suggestion'} onSelect={() => form.setKind('suggestion')} icon={<Lightbulb className="h-6 w-6" />} label="Suggestion" />
      </div>
      <label className="block space-y-1.5">
        <span className="text-sm font-medium">What happened, or what would help?</span>
        <Textarea value={form.words} onChange={(e) => form.setWords(e.target.value)} maxLength={MAX_WORDS} rows={6} />
      </label>
      <ScreenshotPicker value={form.screenshots} onChange={form.setScreenshots} />
      {outcome?.kind === 'refused' && (
        <p role="alert" className="text-sm text-red-700">
          {outcome.message}
        </p>
      )}
      <Button onClick={() => void form.submit()} disabled={form.busy || !form.ready || !form.kind || !form.words.trim()} className="w-full">
        {form.busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Send
      </Button>
      <p className="text-xs text-slate-500">
        We record which page you were on, your device, the app's version, any recent error messages and the time, so we can find the problem. Your name comes from your sign-in.
      </p>
    </div>
  );
}

/**
 * The form on a page of its own (App Feedback's report page for an app with no API), not in
 * the header's side panel. Close starts a new report.
 */
export function FeedbackForm(props: FeedbackFormProps) {
  const form = useFeedbackForm(props);
  return <FeedbackFormView form={form} onDone={form.clear} myReportsLink={props.myReportsLink} />;
}

/** App Feedback's My reports, in a new tab (Edge, where the Entra sign-in lives, from an iPad Home Screen app). */
function MyReportsLink() {
  return (
    <OutboundLink href={MY_REPORTS_URL} openIn="edge" className="inline-flex items-center gap-1 font-medium text-primary underline-offset-2 hover:underline">
      See my reports <ExternalLink className="h-3.5 w-3.5" aria-hidden />
    </OutboundLink>
  );
}

function KindButton({ selected, onSelect, icon, label }: { selected: boolean; onSelect: () => void; icon: ReactNode; label: string }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={cn(
        'flex min-h-20 flex-col items-center justify-center gap-1.5 rounded-lg border-2 text-base font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500',
        selected ? 'border-orange-500 bg-orange-50 text-slate-900' : 'border-slate-200 text-slate-600 hover:border-slate-300',
      )}
    >
      {icon}
      {label}
    </button>
  );
}
